import React, { useEffect, useRef, useState } from "react";
import Dryrose from "../images/corner/corner-dry-rose-art.webp";
import DefaultStoryImage from "../images/good-karma.png";
import Button from "../components/Button";
import moment from "moment";
import { useHistory } from "react-router";
import StrapiImage from "../components/StrapiImage";
import FullPageLoader from "../components/FullPageLoader";

const getPlainText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.replace(/\s+/g, " ").trim();
  if (Array.isArray(value)) {
    return value
      .map((item) => getPlainText(item))
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (typeof value === "object") {
    return [value.text, getPlainText(value.children)]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  return "";
};

const excerpt = (text, max = 140) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, "") + "…";
};

const SEARCH_STORAGE_KEY = "wineStoriesSearch";

const readSavedSearch = () => {
  try {
    return window.localStorage.getItem(SEARCH_STORAGE_KEY) || "";
  } catch (e) {
    return "";
  }
};

const writeSavedSearch = (value) => {
  try {
    if (value) {
      window.localStorage.setItem(SEARCH_STORAGE_KEY, value);
    } else {
      window.localStorage.removeItem(SEARCH_STORAGE_KEY);
    }
  } catch (e) {
    // storage unavailable (private mode, etc.) - search just won't persist
  }
};

const WineStories = (props) => {
  const history = useHistory();
  const [query, setQuery] = useState(readSavedSearch);
  const searchRef = useRef(null);
  const [searchStuck, setSearchStuck] = useState(false);
  const [searchLeft, setSearchLeft] = useState(0);

  // position: sticky doesn't work here because html/body both set
  // overflow-x: hidden, so we pin the search box to the top ourselves. The
  // in-flow wrapper keeps its space so the page doesn't jump.
  useEffect(() => {
    const onScroll = () => {
      const el = searchRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setSearchStuck(rect.top <= 16);
      setSearchLeft(rect.left);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Persist the search so it survives a refresh...
  useEffect(() => {
    writeSavedSearch(query);
  }, [query]);

  // ...but clear it when leaving this page (a refresh doesn't unmount React)
  useEffect(() => {
    return () => writeSavedSearch("");
  }, []);

  useEffect(() => {
    document.title = "Auburn Road - Wine Stories";
    return () => {
      document.title = "Auburn Road Vineyards";
    };
  }, []);

  // null/undefined means the Strapi request is still in flight
  if (props.stories === null || props.stories === undefined) {
    return <FullPageLoader />;
  }

  const getTitle = (story) =>
    story?.Title || story?.title || story?.fields?.title || "";
  const getUniqueUrl = (story) =>
    story?.unique_url || story?.uniqueUrl || story?.fields?.uniqueUrl || null;
  const getPublishDate = (story) =>
    story?.publish_date ||
    story?.publishDate ||
    story?.fields?.publishDate ||
    story?.createdAt ||
    story?.sys?.createdAt ||
    null;
  const getImageUrl = (story) => {
    let image = StrapiImage(story?.story_image);
    if (!image) return DefaultStoryImage;
    if (typeof image === "string") return image || DefaultStoryImage;
    if (image?.url) return image.url;
    if (image?.formats?.large?.url) return image.formats.large.url;
    if (image?.formats?.medium?.url) return image.formats.medium.url;
    if (image?.formats?.small?.url) return image.formats.small.url;
    if (image?.formats?.thumbnail?.url) return image.formats.thumbnail.url;
    if (image?.fields?.file?.url) return image.fields.file.url;
    return DefaultStoryImage;
  };
  const getBlurb = (story) => {
    const title = getTitle(story);
    const blurb = getPlainText(
      story?.blurb || story?.attributes?.blurb || story?.fields?.blurb
    );
    if (blurb && blurb !== title) return excerpt(blurb);
    const content = getPlainText(
      story?.content || story?.attributes?.content || story?.fields?.content
    );
    if (content && content !== title) return excerpt(content);
    return blurb;
  };
  const getStoryPath = (story) => {
    const slug = (getTitle(story) || "").split(" ").join("-").toLowerCase();
    return "/" + (getUniqueUrl(story) || slug);
  };

  const sortedStories = (props.stories || []).slice().sort((a, b) => {
    return moment(getPublishDate(b)) - moment(getPublishDate(a));
  });
  const searchTerm = query.trim().toLowerCase();
  const isSearching = searchTerm.length > 0;
  const searchResults = isSearching
    ? sortedStories.filter((story) => {
        const title = getTitle(story).toLowerCase();
        return searchTerm.split(/\s+/).every((word) => title.includes(word));
      })
    : [];
  const featured = sortedStories[0];
  const remaining = sortedStories.slice(1);

  const goToStory = (story) => history.push(getStoryPath(story));

  const renderCard = (story, { featuredCard = false, key } = {}) => {
    const image = getImageUrl(story);
    const blurb = getBlurb(story);
    const style = {
      "--bg-image": `url(${image})`,
    };
    return (
      <article
        key={key}
        className={`wine-story-wrap story-card${
          featuredCard ? " story-card--featured" : ""
        }`}
      >
        <div
          className="story-image pointer"
          style={style}
          bg-image={image}
          onClick={() => goToStory(story)}
          role="presentation"
        />
        <div className="story-card__copy">
          <span className="story-card__tags">
            {featuredCard && <span className="story-card__tag">Latest</span>}
            <span className="story-card__tag">
              {moment(getPublishDate(story)).format("MMMM Do, YYYY")}
            </span>
          </span>
          <h3
            className="story-card__title pointer"
            onClick={() => goToStory(story)}
          >
            {getTitle(story)}
          </h3>
          {blurb ? <p className="story-card__blurb blurb">{blurb}</p> : null}
          <Button
            onClick={() => goToStory(story)}
            className="continue-reading"
            small
            type="plain"
          >
            Continue reading
          </Button>
        </div>
      </article>
    );
  };

  return (
    <>
      <img className="corner-dry-rose" src={Dryrose} alt="" />
      <div className="container stories-page p-t-100">
        <section className="stories-hero">
          <p className="stories-hero__kicker">Auburn Road Vineyards</p>
          <h1 className="huge-text-no-quote mb-3">Wine Stories</h1>
          <p className="stories-hero__intro">
            Dispatches from the vineyard: harvest notes, Italy trips, and the
            long tale of making wine in New Jersey.
          </p>
        </section>

        {sortedStories.length ? (
          <div className="stories-search-wrap" ref={searchRef}>
            <div
              className={`stories-search${searchStuck ? " is-stuck" : ""}`}
              style={searchStuck ? { left: searchLeft } : undefined}
            >
              <svg
                className="stories-search__icon"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                aria-hidden="true"
              >
                <circle
                  cx="10.5"
                  cy="10.5"
                  r="6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M15.5 15.5L21 21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <input
                className="stories-search__input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stories"
                aria-label="Search wine stories"
              />
            </div>
          </div>
        ) : null}

        {isSearching ? (
          searchResults.length ? (
            <div className="story-grid">
              {searchResults.map((story, i) =>
                renderCard(story, { key: "wine-story-result-" + i })
              )}
            </div>
          ) : (
            <p className="stories-search__empty">
              No stories match “{query.trim()}”.
            </p>
          )
        ) : sortedStories.length ? (
          <>
            <div className="first-story">
              {renderCard(featured, { featuredCard: true })}
            </div>
            {remaining.length > 0 && (
              <div className="story-grid">
                {remaining.map((story, i) =>
                  renderCard(story, { key: "wine-story-" + i })
                )}
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
};

export default WineStories;
